/**
 * Sentry, loaded only when a DSN is configured (createCrashReporter). The SDK
 * is imported by a variable specifier, so builds without a DSN never contain it.
 * Captures made while it loads are replayed once it is ready.
 */
import { optionalModule, type Loader } from '../lazy.js';
import type { CrashKind, CrashReport, CrashReporter } from './types.js';

export interface SentryLike {
  init(o: { dsn: string; release?: string; environment?: string; sampleRate?: number; defaultIntegrations?: false; sendDefaultPii?: boolean }): void;
  captureException(e: unknown, hint?: { tags?: Record<string, string>; extra?: Record<string, unknown> }): string;
  flush(timeoutMs?: number): Promise<boolean>;
}

export const SENTRY_MODULE = '@sentry/browser';

export interface SentryConfig {
  dsn: string;
  release?: string;
  environment?: string;
  sampleRate?: number;
  load?: Loader<SentryLike>;
}

export class SentryReporter implements CrashReporter {
  private sdk: SentryLike | null = null;
  private pending: [unknown, CrashKind, CrashReport['context']][] = [];
  readonly ready: Promise<void>;

  constructor(c: SentryConfig, log: Pick<Console, 'warn'> = console) {
    const load = c.load ?? optionalModule<SentryLike>(SENTRY_MODULE);
    this.ready = load().then(
      (sdk) => {
        // our own global handlers feed capture(); Sentry's would double-report
        sdk.init({ dsn: c.dsn, release: c.release, environment: c.environment, sampleRate: c.sampleRate ?? 1, defaultIntegrations: false, sendDefaultPii: false });
        this.sdk = sdk;
        for (const [e, k, ctx] of this.pending.splice(0)) this.send(sdk, e, k, ctx);
      },
      // an optional SDK that fails to load (ad blocker, offline) must not become a crash itself
      (e: unknown) => {
        this.pending = [];
        log.warn('[crash] Sentry SDK unavailable; API reporting continues', e);
      },
    );
  }

  capture(error: unknown, kind: CrashKind = 'manual', context?: CrashReport['context']): void {
    if (this.sdk) this.send(this.sdk, error, kind, context);
    else if (this.pending.length < 20) this.pending.push([error, kind, context]);
  }

  async flush(): Promise<void> {
    await this.sdk?.flush(2000);
  }

  private send(sdk: SentryLike, error: unknown, kind: CrashKind, context: CrashReport['context']): void {
    sdk.captureException(error, { tags: { kind }, ...(context ? { extra: context } : {}) });
  }
}
